import React from 'react';

// Panel de los drops de articulos.
//
// Se carga un lote de entradas del mismo articulo (enlaces, codigos, comandos
// recompensa: lo que sea que se pueda soltar en el chat) y el servidor las va soltando a
// lo largo del directo (ver apps/articleDrops.js). Aqui solo se dan de alta las
// campañas, se ve como va el reparto y se controla la tanda a mano.
//
// No usa FormComp porque hace falta ver el progreso de cada campaña y tener botones de
// control, cosas que el formulario declarativo no cubre.

// Mismo parseo que hace el servidor, para poder contar las entradas antes de enviarlas.
const parseItems = (raw) => String(raw || '')
    .split(',')
    .map(item => item.trim())
    .filter(item => item.length > 0);

const formatTime = (iso) => {
    if (!iso) return '';
    return new Date(iso).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
};

const AdminDrops = () => {
    const [title, setTitle] = React.useState('');
    const [links, setLinks] = React.useState('');
    const [campaigns, setCampaigns] = React.useState([]);
    const [session, setSession] = React.useState(null);
    const [status, setStatus] = React.useState('');
    const [statusType, setStatusType] = React.useState(''); // 'success' | 'error' | ''
    const [loading, setLoading] = React.useState(false);

    const parsed = parseItems(links);
    // Ya no se deduplica en el servidor (repetir una entrada es legitimo), asi que el
    // aviso de repetidos es lo unico que separa el repeticion queria de la pegada doble.
    const repeated = parsed.length - new Set(parsed).size;

    const refresh = React.useCallback(async () => {
        try {
            const res = await fetch('/api/adminLogin?action=get&type=articleDrops', { method: 'POST' });
            const data = await res.json();
            setCampaigns((data && data.campaigns) || []);
            setSession((data && data.session) || null);
        } catch (err) {
            setStatus('No se pudo leer el estado de los drops');
            setStatusType('error');
        }
    }, []);

    React.useEffect(() => { refresh(); }, [refresh]);

    const handleCreate = async () => {
        const cleanTitle = title.trim();
        if (!cleanTitle) {
            setStatus('Ponle un título al artículo');
            setStatusType('error');
            return;
        }
        if (parsed.length === 0) {
            setStatus('No has puesto ninguna entrada');
            setStatusType('error');
            return;
        }

        setLoading(true);
        setStatus('');
        setStatusType('');
        try {
            const formData = new FormData();
            formData.append('title', cleanTitle);
            formData.append('links', links);
            const res = await fetch('/api/adminLogin?action=create&type=articleDrops', {
                method: 'POST',
                body: formData
            });
            const data = await res.json();
            if (data && data.success) {
                setStatus('Campaña "' + cleanTitle + '" creada con ' + data.links + ' entradas.');
                setStatusType('success');
                setTitle('');
                setLinks('');
                await refresh();
            } else {
                setStatus((data && data.error) || 'Error al crear la campaña');
                setStatusType('error');
            }
        } catch (err) {
            setStatus('Error al crear la campaña');
            setStatusType('error');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (campaign) => {
        if (!window.confirm('¿Borrar la campaña "' + campaign.title + '"? Se pierden los enlaces que queden sin repartir.')) return;
        setLoading(true);
        try {
            const formData = new FormData();
            formData.append('id', campaign.id);
            const res = await fetch('/api/adminLogin?action=delete&type=articleDrops', {
                method: 'POST',
                body: formData
            });
            const data = await res.json();
            if (data && data.success) {
                setStatus('Campaña borrada.');
                setStatusType('success');
            } else {
                setStatus((data && data.error) || 'Error al borrar la campaña');
                setStatusType('error');
            }
            await refresh();
        } catch (err) {
            setStatus('Error al borrar la campaña');
            setStatusType('error');
        } finally {
            setLoading(false);
        }
    };

    const handleControl = async (op, extra = {}) => {
        setLoading(true);
        setStatus('');
        setStatusType('');
        try {
            const formData = new FormData();
            formData.append('op', op);
            Object.keys(extra).forEach(key => formData.append(key, extra[key]));
            const res = await fetch('/api/adminLogin?action=dropsControl', {
                method: 'POST',
                body: formData
            });
            const data = await res.json();
            if (data && data.success) {
                if (op === 'start') {
                    setStatus(data.scheduled > 0
                        ? data.scheduled + ' drops repartidos por el directo.'
                        : 'No hay enlaces pendientes que repartir.');
                } else if (op === 'stop') {
                    setStatus('Tanda parada; los drops sin repartir vuelven a la cola.');
                } else if (op === 'testAlert') {
                    setStatus('Alerta de prueba enviada al overlay (' +
                        (data.drop.platform === 'youtube' ? 'YouTube' : 'Twitch') +
                        '). No se ha gastado ningún enlace ni se ha escrito en el chat.');
                } else {
                    setStatus('"' + data.drop.title + '" soltado en ' + (data.drop.platform === 'youtube' ? 'YouTube' : 'Twitch') + '.');
                }
                setStatusType('success');
            } else {
                setStatus((data && data.error) || 'No se pudo completar la operación');
                setStatusType('error');
            }
            await refresh();
        } catch (err) {
            setStatus('Error al controlar los drops');
            setStatusType('error');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className='heroes formOver'>
            <h1>Drops de artículos</h1>

            <div className='formFieldRow'>
                <label className='formFieldLabel heroes'>Título del artículo</label>
                <input
                    className='userSearch'
                    placeholder='Spray de Calavela de Brawl Stars'
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                />
            </div>
            <div className='formFieldRow'>
                <label className='formFieldLabel heroes'>Enlaces o códigos (separados por comas)</label>
                <textarea
                    className='userSearch'
                    rows='5'
                    placeholder='https://ejemplo.com/uno, https://ejemplo.com/dos&#10;...o códigos sueltos: ABC-123, XYZ-456&#10;...o un comando recompensa repetido: !GEL, !GEL, !GEL'
                    value={links}
                    onChange={(e) => setLinks(e.target.value)}
                />
            </div>
            <p style={{ fontSize: 13, color: '#ccc' }}>
                Se guardarán <strong>{parsed.length}</strong> entradas. Vale cualquier cosa que se pueda soltar en el chat:
                un enlace, un código o un comando recompensa. Solo se descartan los huecos que deja una coma de más.
                Cada entrada cae una sola vez, repartida a lo largo del directo, al azar entre Twitch y YouTube.
                {repeated > 0 ? (
                    <span style={{ color: '#e0b000' }}>
                        {' '}Hay <strong>{repeated}</strong> repetidas: se guardan tal cual y caerán una vez cada una.
                        Si ha sido al pegar, quítalas.
                    </span>
                ) : null}
            </p>
            <div className='navTops heroes unselectable'>
                <div onClick={() => { if (!loading) { handleCreate(); } }}>Crear campaña</div>
            </div>

            <h2>Tanda en curso</h2>
            {session ? (
                <p style={{ fontSize: 14 }}>
                    {session.running
                        ? 'Repartiendo: quedan ' + session.scheduled + ' drops' +
                          (session.nextAt ? '. El próximo ("' + session.nextTitle + '") a las ' + formatTime(session.nextAt) : '')
                        : 'Parada. Arranca sola cuando empiece el directo de Twitch.'}
                    {' · Twitch: ' + (session.twitchLive ? 'en directo' : 'apagado')}
                    {' · YouTube: ' + (session.youtubeLive ? 'en directo' : 'apagado')}
                </p>
            ) : <p>Cargando...</p>}
            <div className='navTops heroes unselectable'>
                <div onClick={() => { if (!loading) { handleControl('start'); } }}>Iniciar ahora</div>
                <div onClick={() => { if (!loading) { handleControl('stop'); } }}>Parar</div>
                <div onClick={() => { if (!loading) { handleControl('fireNow'); } }}>Lanzar drop ya</div>
                <div onClick={() => { if (!loading) { refresh(); } }}>Actualizar</div>
            </div>

            <h2>Probar la alerta</h2>
            <p style={{ fontSize: 13, color: '#ccc' }}>
                Dispara solo la alerta del overlay, con el directo apagado y sin gastar ningún
                enlace ni escribir en el chat. Es para colocar y ajustar el browser source de OBS
                (<span className='userLink'>?streamGadgets=rewardsAlertStream</span>).
                Ojo: la alerta <strong>solo</strong> se pinta en ese gadget; en
                {' '}<span className='userLink'>rewardsHistory</span> y
                {' '}<span className='userLink'>rewardsHistoryStream</span> suena el aviso pero no se ve nada.
            </p>
            <div className='navTops heroes unselectable'>
                <div onClick={() => { if (!loading) { handleControl('testAlert', { platform: 'twitch' }); } }}>Probar alerta (Twitch)</div>
                <div onClick={() => { if (!loading) { handleControl('testAlert', { platform: 'youtube' }); } }}>Probar alerta (YouTube)</div>
            </div>

            <h2>Campañas</h2>
            {campaigns.length === 0 ? <p>Todavía no hay ninguna campaña.</p> : (
                <div>
                    {campaigns.map(campaign => (
                        <div key={campaign.id} className='formFieldRow' style={{ alignItems: 'center' }}>
                            <span style={{ flex: 1 }}>
                                <strong>{campaign.title}</strong>
                                {campaign.status === 'done' ? ' (terminada)' : ''}
                                <br />
                                <span style={{ fontSize: 13, color: '#ccc' }}>
                                    {campaign.counts.sent} / {campaign.total} entregados
                                    {' · Twitch ' + campaign.byPlatform.twitch + ' · YouTube ' + campaign.byPlatform.youtube}
                                    {campaign.counts.scheduled > 0 ? ' · ' + campaign.counts.scheduled + ' programados' : ''}
                                </span>
                            </span>
                            <div className='navTops heroes unselectable'>
                                <div onClick={() => { if (!loading) { handleDelete(campaign); } }}>Borrar</div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {loading ? <p>Cargando...</p> : null}
            {status ? (
                <p
                    role={statusType === 'error' ? 'alert' : 'status'}
                    style={{
                        padding: '8px 12px',
                        borderRadius: 4,
                        fontWeight: 600,
                        color: statusType === 'success' ? '#0a6b0a' : statusType === 'error' ? '#8a1a1a' : 'inherit',
                        backgroundColor: statusType === 'success' ? '#d9f5d9' : statusType === 'error' ? '#fdecea' : 'transparent'
                    }}
                >
                    {status}
                </p>
            ) : null}
        </div>
    );
};

export default AdminDrops;
